import { useEffect, useState } from "react";
import { api, API, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../components/ui/alert-dialog";
import { Plus, Loader2, SunMedium, BatteryCharging, FileText, Upload, Pencil, Trash2, ExternalLink, Plug, Image as ImageIcon, Palette, Save } from "lucide-react";
import { VetrinaSlots } from "../components/VetrinaSlots";

const EMPTY = { category: "pannello", brand: "Torri", model: "", potenza_wp: "", efficienza: "", larghezza_mm: "", lunghezza_mm: "", tecnologia: "", tipo_tensione: "LV", capacita_kwh: "", tensione_v: "", chimica: "LiFePO4", moduli_min: "", moduli_max: "", potenza_kw: "", prezzo_eur: "", prezzo_eur_kwp: "", prezzo_eur_kwh: "", datasheet_url: "", descrizione: "", listino_codice: "", active: true, notes: "" };

const numOrNull = (v) => (v === "" || v === null || v === undefined ? null : Number(v));

export default function Products() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploadingId, setUploadingId] = useState(null);
  const [uploadingPhotoId, setUploadingPhotoId] = useState(null);
  const [coloriImgs, setColoriImgs] = useState([]);
  const [uploadingColoreDs, setUploadingColoreDs] = useState(null);
  const [uploadingColore, setUploadingColore] = useState(null);
  const [colori, setColori] = useState({ total_black: "", ecored: "" });
  const [savingColori, setSavingColori] = useState(false);
  const [listino, setListino] = useState([]);
  useEffect(() => { api.get("/listino").then((r) => setListino(r.data.items || [])).catch(() => {}); }, []);
  const token = localStorage.getItem("token");

  const load = () => { setLoading(true); api.get("/products").then((r) => setProducts(r.data)).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);
  useEffect(() => { api.get("/settings/colori-pannello").then((r) => { setColori({ total_black: r.data.colori.total_black, ecored: r.data.colori.ecored }); setColoriImgs(r.data.list || []); }).catch(() => {}); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({ ...EMPTY, ...Object.fromEntries(Object.entries(p).map(([k, v]) => [k, v === null ? "" : v])), active: p.active !== false });
    setOpen(true);
  };

  const save = async () => {
    if (!form.model.trim()) { toast.error("Inserisci il modello"); return; }
    setSaving(true);
    const payload = {
      category: form.category, brand: form.brand, model: form.model, tecnologia: form.tecnologia || null,
      notes: form.notes, active: form.active, descrizione: form.descrizione || null,
      datasheet_url: form.datasheet_url ? form.datasheet_url.trim() : null,
      listino_codice: form.listino_codice || null,
      potenza_wp: form.category === "pannello" ? numOrNull(form.potenza_wp) : null,
      efficienza: form.category === "pannello" ? numOrNull(form.efficienza) : null,
      larghezza_mm: form.category === "pannello" ? numOrNull(form.larghezza_mm) : null,
      lunghezza_mm: form.category === "pannello" ? numOrNull(form.lunghezza_mm) : null,
      prezzo_eur_kwp: form.category === "pannello" ? numOrNull(form.prezzo_eur_kwp) : null,
      tipo_tensione: form.category === "batteria" ? form.tipo_tensione : null,
      capacita_kwh: form.category === "batteria" ? numOrNull(form.capacita_kwh) : null,
      tensione_v: form.category === "batteria" ? numOrNull(form.tensione_v) : null,
      chimica: form.category === "batteria" ? form.chimica : null,
      moduli_min: form.category === "batteria" ? numOrNull(form.moduli_min) : null,
      moduli_max: form.category === "batteria" ? numOrNull(form.moduli_max) : null,
      prezzo_eur_kwh: form.category === "batteria" ? numOrNull(form.prezzo_eur_kwh) : null,
      potenza_kw: form.category === "ricarica" ? numOrNull(form.potenza_kw) : null,
      prezzo_eur: form.category === "ricarica" ? numOrNull(form.prezzo_eur) : null,
    };
    try {
      if (editing) await api.put(`/products/${editing.id}`, payload);
      else await api.post("/products", payload);
      toast.success(editing ? "Prodotto aggiornato" : "Prodotto creato");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/products/${id}`); toast.success("Prodotto eliminato"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const uploadDatasheet = async (id, files) => {
    const file = files?.[0]; if (!file) return;
    setUploadingId(id);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/products/${id}/datasheet`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Scheda tecnica caricata"); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setUploadingId(null); }
  };

  const uploadPhoto = async (id, files) => {
    const file = files?.[0]; if (!file) return;
    setUploadingPhotoId(id);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/products/${id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Foto caricata"); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setUploadingPhotoId(null); }
  };

  const uploadColoreImage = async (key, files) => {
    const file = files?.[0]; if (!file) return;
    setUploadingColore(key);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post(`/settings/colori-pannello/${key}/image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setColoriImgs(data.list || []);
      toast.success("Immagine colore caricata");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setUploadingColore(null); }
  };

  const uploadColoreDatasheet = async (key, files) => {
    const file = files?.[0]; if (!file) return;
    setUploadingColoreDs(key);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post(`/settings/colori-pannello/${key}/datasheet`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setColoriImgs(data.list || []);
      toast.success("Scheda tecnica colore caricata");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setUploadingColoreDs(null); }
  };

  const saveColori = async () => {
    setSavingColori(true);
    try {
      await api.put("/settings/colori-pannello", { total_black: Number(colori.total_black) || 0, ecored: Number(colori.ecored) || 0 });
      toast.success("Sovrapprezzi colore salvati");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingColori(false); }
  };

  const pannelli = products.filter((p) => p.category === "pannello");
  const batterie = products.filter((p) => p.category === "batteria");
  const ricariche = products.filter((p) => p.category === "ricarica");

  return (
    <div>
      <VetrinaSlots />
      <details className="mt-6 group" data-testid="internal-settings">
        <summary className="cursor-pointer list-none px-4 lg:px-8 py-3 text-sm font-semibold text-muted-foreground hover:text-foreground flex items-center gap-2 border-t border-border">⚙︎ Impostazioni interne — fallback tecnico &amp; colori (non visibili al cliente)</summary>
      <header className="sticky top-14 lg:top-0 z-10 h-16 border-b border-border bg-background flex items-center justify-between px-4 lg:px-8">
        <div>
          <h1 className="text-xl font-heading font-bold">Prodotti di flusso</h1>
          <p className="text-xs text-muted-foreground">Parametri di dimensionamento e schede tecniche</p>
        </div>
        <Button onClick={openNew} data-testid="new-product-btn" className="font-semibold"><Plus className="h-4 w-4 mr-1.5" /> Nuovo prodotto</Button>
      </header>

      <div className="p-4 lg:p-8 space-y-8">
        {loading ? (
          <div className="flex h-40 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
        ) : (
          <>
            <Section title="Pannelli fotovoltaici" icon={SunMedium}>
              {pannelli.map((p) => <ProductCard key={p.id} p={p} token={token} onEdit={openEdit} onDelete={remove} onUpload={uploadDatasheet} uploading={uploadingId === p.id} onUploadPhoto={uploadPhoto} uploadingPhoto={uploadingPhotoId === p.id} />)}
              {pannelli.length === 0 && <Empty label="Nessun pannello registrato" />}
            </Section>
            <Section title="Accumuli / Batterie" icon={BatteryCharging}>
              {batterie.map((p) => <ProductCard key={p.id} p={p} token={token} onEdit={openEdit} onDelete={remove} onUpload={uploadDatasheet} uploading={uploadingId === p.id} onUploadPhoto={uploadPhoto} uploadingPhoto={uploadingPhotoId === p.id} />)}
              {batterie.length === 0 && <Empty label="Nessuna batteria registrata" />}
            </Section>
            <Section title="Stazioni di ricarica" icon={Plug}>
              {ricariche.map((p) => <ProductCard key={p.id} p={p} token={token} onEdit={openEdit} onDelete={remove} onUpload={uploadDatasheet} uploading={uploadingId === p.id} onUploadPhoto={uploadPhoto} uploadingPhoto={uploadingPhotoId === p.id} />)}
              {ricariche.length === 0 && <Empty label="Nessuna stazione di ricarica registrata" />}
            </Section>
          </>
        )}
      </div>
      </details>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg bg-popover max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Modifica prodotto" : "Nuovo prodotto"}</DialogTitle>
            <DialogDescription className="sr-only">Parametri di dimensionamento del prodotto</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Categoria">
                <Select value={form.category} onValueChange={(v) => set("category", v)} disabled={!!editing}>
                  <SelectTrigger data-testid="product-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="pannello">Pannello FV</SelectItem><SelectItem value="batteria">Batteria</SelectItem><SelectItem value="ricarica">Stazione di ricarica</SelectItem></SelectContent>
                </Select>
              </Field>
              <Field label="Marca"><Input value={form.brand} onChange={(e) => set("brand", e.target.value)} data-testid="product-brand-input" /></Field>
            </div>
            <Field label="Modello *"><Input value={form.model} onChange={(e) => set("model", e.target.value)} data-testid="product-model-input" placeholder="es. Integra Black 450" /></Field>

            {form.category === "pannello" ? (
              <div className="grid grid-cols-2 gap-4">
                <Field label="Potenza (Wp)"><Input type="number" value={form.potenza_wp} onChange={(e) => set("potenza_wp", e.target.value)} data-testid="product-potenza-input" placeholder="450" /></Field>
                <Field label="Efficienza (%)"><Input type="number" value={form.efficienza} onChange={(e) => set("efficienza", e.target.value)} placeholder="23" /></Field>
                <Field label="Tecnologia"><Input value={form.tecnologia} onChange={(e) => set("tecnologia", e.target.value)} placeholder="TOPCon" /></Field>
                <Field label="Prezzo (€/kWp)"><Input type="number" value={form.prezzo_eur_kwp} onChange={(e) => set("prezzo_eur_kwp", e.target.value)} data-testid="product-prezzo-kwp-input" placeholder="240" /></Field>
                <Field label="Larghezza (mm)"><Input type="number" value={form.larghezza_mm} onChange={(e) => set("larghezza_mm", e.target.value)} data-testid="product-larghezza-input" placeholder="1134" /></Field>
                <Field label="Lunghezza (mm)"><Input type="number" value={form.lunghezza_mm} onChange={(e) => set("lunghezza_mm", e.target.value)} data-testid="product-lunghezza-input" placeholder="1962" /></Field>
              </div>
            ) : form.category === "batteria" ? (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Tipo tensione">
                    <Select value={form.tipo_tensione} onValueChange={(v) => set("tipo_tensione", v)}>
                      <SelectTrigger data-testid="product-tensione-select"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="LV">LV — Bassa tensione (1-2 batterie)</SelectItem><SelectItem value="HV">HV — Alta tensione (3+ batterie)</SelectItem></SelectContent>
                    </Select>
                  </Field>
                  <Field label="Capacità modulo (kWh)"><Input type="number" value={form.capacita_kwh} onChange={(e) => set("capacita_kwh", e.target.value)} data-testid="product-capacita-input" placeholder="5.12" /></Field>
                  <Field label="Tensione (V)"><Input type="number" value={form.tensione_v} onChange={(e) => set("tensione_v", e.target.value)} placeholder="51.2" /></Field>
                  <Field label="Chimica"><Input value={form.chimica} onChange={(e) => set("chimica", e.target.value)} placeholder="LiFePO4" /></Field>
                  <Field label="Moduli min"><Input type="number" value={form.moduli_min} onChange={(e) => set("moduli_min", e.target.value)} placeholder="1" /></Field>
                  <Field label="Moduli max"><Input type="number" value={form.moduli_max} onChange={(e) => set("moduli_max", e.target.value)} placeholder="2" /></Field>
                  <Field label="Prezzo (€/kWh)"><Input type="number" value={form.prezzo_eur_kwh} onChange={(e) => set("prezzo_eur_kwh", e.target.value)} data-testid="product-prezzo-kwh-input" placeholder="265" /></Field>
                </div>
              </>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                <Field label="Potenza (kW)">
                  <Select value={String(form.potenza_kw || "")} onValueChange={(v) => set("potenza_kw", v)}>
                    <SelectTrigger data-testid="product-potenzakw-select"><SelectValue placeholder="7 / 11 / 22" /></SelectTrigger>
                    <SelectContent><SelectItem value="7">7 kW (monofase)</SelectItem><SelectItem value="11">11 kW</SelectItem><SelectItem value="22">22 kW</SelectItem></SelectContent>
                  </Select>
                </Field>
                <Field label="Prezzo (€)"><Input type="number" value={form.prezzo_eur} onChange={(e) => set("prezzo_eur", e.target.value)} data-testid="product-prezzo-input" placeholder="1500" /></Field>
              </div>
            )}
            <Field label="Prodotto del listino rete.torri.solar (foto ufficiale)">
              <select value={form.listino_codice || ""} onChange={(e) => set("listino_codice", e.target.value)} className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm" data-testid="product-listino-codice-select">
                <option value="">Automatico (stesso nome) / nessuno</option>
                {listino.map((it) => <option key={it.codice} value={it.codice}>{it.codice}{it.descrizione ? ` — ${String(it.descrizione).slice(0, 60)}` : ""}{it.immagine_url ? "" : " (senza foto)"}</option>)}
              </select>
              <p className="text-[11px] text-muted-foreground mt-1">Nel configuratore e nei PDF viene usata la foto ufficiale del listino; la foto caricata qui resta solo come riserva.</p>
            </Field>
            <Field label="Link scheda tecnica (URL su internet)"><Input type="url" value={form.datasheet_url} onChange={(e) => set("datasheet_url", e.target.value)} data-testid="product-datasheet-url-input" placeholder="https://..." /></Field>
            <Field label="Descrizione prodotto (mostrata nel PDF proposta)"><Textarea rows={4} value={form.descrizione} onChange={(e) => set("descrizione", e.target.value)} data-testid="product-descrizione-input" placeholder="Descrizione completa del prodotto: caratteristiche, vantaggi, garanzie…" /></Field>
            <Field label="Note"><Textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} /></Field>
            <div className="flex items-center gap-2">
              <Switch checked={form.active} onCheckedChange={(v) => set("active", v)} data-testid="product-active-switch" />
              <span className="text-sm">Attivo (usato per il dimensionamento)</span>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={save} disabled={saving} data-testid="save-product-btn" className="font-semibold">{saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <div>
      <h2 className="text-sm uppercase tracking-[0.15em] text-muted-foreground font-bold mb-4 flex items-center gap-2"><Icon className="h-4 w-4 text-primary" strokeWidth={1.5} /> {title}</h2>
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">{children}</div>
    </div>
  );
}

function Field({ label, children }) {
  return <div className="space-y-1.5"><Label className="text-xs uppercase tracking-widest text-muted-foreground">{label}</Label>{children}</div>;
}

function Empty({ label }) {
  return <div className="col-span-full text-sm text-muted-foreground border border-dashed border-border rounded p-6 text-center">{label}</div>;
}

function ProductCard({ p, token, onEdit, onDelete, onUpload, uploading, onUploadPhoto, uploadingPhoto }) {
  const eur = (v) => (v === null || v === undefined ? "—" : Number(v).toLocaleString("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }));
  const params = p.category === "pannello"
    ? [["Potenza", p.potenza_wp ? `${p.potenza_wp} Wp` : "—"], ["Efficienza", p.efficienza ? `${p.efficienza}%` : "—"], ["Tecnologia", p.tecnologia || "—"], ["Prezzo", p.prezzo_eur_kwp ? `${p.prezzo_eur_kwp} €/kWp` : "—"]]
    : p.category === "ricarica"
    ? [["Potenza", p.potenza_kw ? `${p.potenza_kw} kW` : "—"], ["Prezzo", eur(p.prezzo_eur)]]
    : [["Capacità modulo", p.capacita_kwh ? `${p.capacita_kwh} kWh` : "—"], ["Tensione", p.tensione_v ? `${p.tensione_v} V` : "—"], ["Chimica", p.chimica || "—"], ["Moduli", `${p.moduli_min ?? "—"} - ${p.moduli_max ?? "—"}`], ["Prezzo", p.prezzo_eur_kwh ? `${p.prezzo_eur_kwh} €/kWh` : "—"]];
  const inputId = `ds-${p.id}`;
  const photoId = `ph-${p.id}`;
  return (
    <Card className="rounded border-border p-4" data-testid={`product-card-${p.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-3">
          {(p.official_photo_url || p.photo_url) && <img src={p.official_photo_url || p.photo_url} title={p.official_photo_url ? "Foto ufficiale listino" : "Foto catalogo"} alt={p.model} className="h-12 w-12 rounded object-cover border border-border" data-testid={`product-photo-${p.id}`} />}
          <div>
            <div className="font-heading font-semibold">{p.model}</div>
            <div className="text-xs text-muted-foreground">{p.brand}</div>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          {p.category === "batteria" && p.tipo_tensione && <Badge variant="outline" className="border-primary/40 text-primary font-normal">{p.tipo_tensione}</Badge>}
          {!p.active && <Badge variant="outline" className="border-border text-muted-foreground font-normal">Disattivo</Badge>}
        </div>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
        {params.map(([k, v]) => (
          <div key={k}><div className="text-[10px] uppercase tracking-widest text-muted-foreground">{k}</div><div className="font-medium">{v}</div></div>
        ))}
      </div>

      {p.notes && <p className="text-xs text-muted-foreground mt-3">{p.notes}</p>}

      {p.datasheet_url && (
        <a href={p.datasheet_url} target="_blank" rel="noopener noreferrer" data-testid={`datasheet-url-link-${p.id}`} className="text-xs text-primary flex items-center gap-1 hover:underline mt-3"><ExternalLink className="h-3.5 w-3.5" /> Scheda tecnica online</a>
      )}

      <div className="mt-4 pt-3 border-t border-border flex items-center justify-between">
        <span className="text-xs text-muted-foreground">Foto prodotto</span>
        <div className="flex items-center gap-1">
          <input id={photoId} type="file" accept="image/*" className="hidden" data-testid={`photo-input-${p.id}`} onChange={(e) => onUploadPhoto(p.id, e.target.files)} />
          <Button variant="ghost" size="sm" onClick={() => document.getElementById(photoId).click()} disabled={uploadingPhoto} data-testid={`upload-photo-${p.id}`} title="Carica foto prodotto">
            {uploadingPhoto ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImageIcon className="h-3.5 w-3.5" />}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => onEdit(p)} data-testid={`edit-product-${p.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
          <AlertDialog>
            <AlertDialogTrigger asChild><Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" data-testid={`delete-product-${p.id}`}><Trash2 className="h-3.5 w-3.5" /></Button></AlertDialogTrigger>
            <AlertDialogContent className="bg-popover">
              <AlertDialogHeader><AlertDialogTitle>Eliminare {p.model}?</AlertDialogTitle><AlertDialogDescription>Azione irreversibile.</AlertDialogDescription></AlertDialogHeader>
              <AlertDialogFooter><AlertDialogCancel>Annulla</AlertDialogCancel><AlertDialogAction onClick={() => onDelete(p.id)} className="bg-destructive text-destructive-foreground">Elimina</AlertDialogAction></AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
    </Card>
  );
}
